/**
 * User-friendly error messages for calendar notes
 */

export const ERROR_MESSAGES = {
  // Validation errors
  INVALID_DATE_FORMAT: 'Please enter a date in the format YYYY-MM-DD (e.g., 2026-07-15)',
  INVALID_TIME_FORMAT: 'Please enter time in 24-hour format HH:mm (e.g., 14:30)',
  DESCRIPTION_REQUIRED: 'Please enter a description for this note',
  DESCRIPTION_TOO_LONG: 'Description cannot be longer than 1000 characters',

  // Authorization errors
  NOTE_NOT_FOUND: 'This note could not be found or you do not have permission to access it',
  OPERATION_ACCESS_DENIED: 'You do not have permission to link this operation',
  UNAUTHORIZED: 'Your session has expired. Please log in again.',

  // Rate limiting
  RATE_LIMIT_CREATE: 'You have created too many notes. Please wait a few minutes and try again.',
  RATE_LIMIT_UPDATE: 'You have updated too many notes. Please wait a few minutes and try again.',
  RATE_LIMIT_DELETE: 'You have deleted too many notes. Please wait a few minutes and try again.',
  RATE_LIMIT_READ: 'You have made too many requests. Please wait a few minutes and try again.',

  // Generic errors
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

/**
 * Map backend error to user-friendly message
 * @param {Error} error - Axios error object
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyError(error) {
  // Network error (no response from server)
  if (!error.response) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  const status = error.response.status;
  const data = error.response.data;

  switch (status) {
    case 400:
      // Validation errors - show specific field errors
      if (data.details && data.details.length > 0) {
        // Combine all validation errors
        const messages = data.details.map(d => {
          if (d.field) {
            return `${d.field}: ${d.message}`;
          }
          return d.message;
        });
        return messages.join('; ');
      }
      return data.error || ERROR_MESSAGES.UNKNOWN_ERROR;

    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED;

    case 403:
      return ERROR_MESSAGES.OPERATION_ACCESS_DENIED;

    case 404:
      return ERROR_MESSAGES.NOTE_NOT_FOUND;

    case 429:
      // Rate limiting - use backend message if available
      return data.message || ERROR_MESSAGES.RATE_LIMIT_READ;

    case 500:
      return ERROR_MESSAGES.UNKNOWN_ERROR;

    default:
      return data.error || ERROR_MESSAGES.UNKNOWN_ERROR;
  }
}

/**
 * Get validation error for specific field
 * @param {Error} error - Axios error object
 * @param {string} fieldName - Field name to get error for
 * @returns {string|null} Error message or null
 */
export function getFieldError(error, fieldName) {
  if (!error.response || error.response.status !== 400) {
    return null;
  }

  const data = error.response.data;
  if (!data.details || !Array.isArray(data.details)) {
    return null;
  }

  const fieldError = data.details.find(d => d.field === fieldName);
  return fieldError ? fieldError.message : null;
}

/**
 * Check if error is a rate limit error
 * @param {Error} error - Axios error object
 * @returns {boolean} True if rate limit error
 */
export function isRateLimitError(error) {
  return error.response && error.response.status === 429;
}

/**
 * Check if error is an authorization error
 * @param {Error} error - Axios error object
 * @returns {boolean} True if authorization error
 */
export function isAuthError(error) {
  return error.response && (error.response.status === 401 || error.response.status === 403);
}

/**
 * Check if error is a validation error
 * @param {Error} error - Axios error object
 * @returns {boolean} True if validation error
 */
export function isValidationError(error) {
  return error.response && error.response.status === 400;
}
